/* global JSX */
import * as React from "react";
import { CheckBox } from "./checkbox";
import { ToolButton } from "./toolButton";
import { IdTitleNode, IIdTitleNode } from "./../../models/common/idTitleNode";
import { Base } from "./../../framework/base";
import { Translations } from "./../../models/translations";

export interface IDropdownAction {
    disabled?: boolean;
    classes?: string;
    divider?: boolean;
    selected?: boolean;
    icon?: React.ReactNode;
    title: string | JSX.Element;
    textTitle?: string;
    onClick: () => void;
}

// DropdownMenu
// ***********************************************************************************************************************
export interface IDropdownMenuProp {
    actions: IDropdownAction[];
    menuRight?: boolean;
    selectMode?: boolean;
    onToggleDropodown: () => void;
}

export class DropdownMenu extends React.Component<IDropdownMenuProp, {}> {
    private dropdownMenuDiv: HTMLDivElement;

    scrollToActiveMenuItem = () => {
        if (!this.props.selectMode || !this.dropdownMenuDiv) return;
        const elements = this.dropdownMenuDiv.getElementsByClassName("selected");
        if (elements.length > 0 && elements[0]) {
            const y = (elements[0] as HTMLDivElement).offsetTop;
            this.dropdownMenuDiv.scrollTo({
                top: y,
                left: 0,
                behavior: "auto"
            });
        }
    };

    componentDidMount(): void {
        this.scrollToActiveMenuItem();
    }

    handleActionClick = (ev: React.MouseEvent, action: IDropdownAction) => {
        ev.preventDefault();
        if (!action) return;
        if (action.disabled) return;
        this.props.onToggleDropodown();
        action.onClick();
    };
    
    render() {
        const props = this.props;
        const actions: (string | JSX.Element)[] = [];
        for (let i = 0; i < props.actions.length; i++) {
            const action = props.actions[i];
            if (action.divider) {
                actions.push(<div key={i} className="dropdown-divider"></div>);
                continue;
            }
            if (action.onClick === null) {
                continue;
            }
            actions.push(<a className={"dropdown-item" + (action.classes ? " " + action.classes : "") + (action.selected ? " selected" : "") + (action.disabled ? " disabled" : "")}
                key={i} href="#" title={typeof action.title === "string" ? action.title : action.textTitle} onClick={(ev) => this.handleActionClick(ev, action) }>{action.title}</a>);
        }
        return (
            <div className={"dropdown-menu show" + (props.menuRight ? " dropdown-menu-right" : "")} ref={(elem) => { this.dropdownMenuDiv = elem; }}>
                {actions}
            </div>
        );
    }
}

// Dropdown
// ***********************************************************************************************************************
export interface IDropdownProp {
    index?: number;
    autoFocus?: boolean;
    autoHeight?: boolean;
    menuRight?: boolean;
    classes?: string;
    buttonClasses?: string;
    disabled?: boolean;
    selectMode?: boolean;
    selectedTitle: string | JSX.Element;
    tooltip?: string;
    actions: IDropdownAction[];
}

export interface IDropdownState {
    open: boolean;
}

export class Dropdown extends React.Component<IDropdownProp, IDropdownState> {
    _isMounted: boolean;
    dropdownId: string;

    constructor(props: IDropdownProp) {
        super(props);
        this.dropdownId = Base.getGuid();
        this.state = {
            open: false
        };
    }

    dropdownClickEvent = (ev: MouseEvent) => {
        if (!this._isMounted) return;
        const element = ev.target as HTMLElement;
        const multiSelect = element.closest("[data-id='" + this.dropdownId + "']");
        if (!multiSelect) {
            this.setState({
                open: false
            });
        }
    };

    handleKeyPress = (e) => {
        const props = this.props;
        //console.log("handleKeyPress", e.key);
        const key: string = e.key;
        if (Base.isNullOrUndefined(key)) return;
        let firstMatchBeforeSelected: IDropdownAction = null;
        let firstMatchAfterSelected: IDropdownAction = null;
        let selectedFound = false;
        for (const action of props.actions) {
            if (action.disabled) continue;
            const title = typeof action.title === "string" ? action.title : action.textTitle;
            if (!title || title.length < 1) continue;
            if (!selectedFound && !firstMatchBeforeSelected && title[0].equalIgnoreCase(key)) {
                firstMatchBeforeSelected = action;
            }
            if (action.selected) {
                selectedFound = true;
                continue;
            }
            if (!selectedFound) continue;
            if (title[0].equalIgnoreCase(key)) {
                firstMatchAfterSelected = action;
                break;
            }
        }
        const firstMatch = firstMatchAfterSelected ?? firstMatchBeforeSelected;
        if (!firstMatch) return;
        firstMatch.onClick();
        return true;
    };

    componentDidMount(): void {
        window.addEventListener("click", this.dropdownClickEvent, true);
        this._isMounted = true;
    }

    componentWillUnmount(): void {
        this._isMounted = false;
        window.removeEventListener("click", this.dropdownClickEvent);
    }

    handleToggleDropdown = () => {
        if (this.props.disabled) return;
        this.setState({
            open: !this.state.open
        });
    };

    handleActionClick = (ev: React.MouseEvent, action: IDropdownAction) => {
        ev.preventDefault();
        if (!action) return;
        if (action.disabled) return;
        this.handleToggleDropdown();
        action.onClick();
    };

    render() {
        const props = this.props;
        const state = this.state;
        const disabled = !!props.disabled;
        const dropdownClasses = "dropdown" + (props.classes ? " " + props.classes : "") + (disabled ? " disabled" : "") + (props.selectMode ? " select" : "") + (props.autoHeight ? " autoHeight" : "");
        const buttonClasses = "btn btn-default dropdown-toggle" + (!Base.isNullOrEmpty(props.buttonClasses) ? " " + props.buttonClasses : "") + (disabled ? " disabled" : "");
        return (
            <div data-id={this.dropdownId} className={dropdownClasses} title={props.tooltip}>
                <button type="button" className={buttonClasses} data-toggle="dropdown" aria-haspopup="true" aria-expanded="false" autoFocus={props.autoFocus} onClick={this.handleToggleDropdown} onKeyPress={props.selectMode ? this.handleKeyPress : null}>
                    <div className="valueContainer">{props.selectedTitle}</div>
                </button>
                {state.open &&
                    <DropdownMenu
                        actions={props.actions}
                        menuRight={props.menuRight}
                        selectMode={props.selectMode}
                        onToggleDropodown={this.handleToggleDropdown}
                    />
                }
            </div>
        );
    }
}

// CheckBoxDropdown
// ***********************************************************************************************************************
export interface ICheckBoxDropdownItem {
    classes?: string;
    disabled?: boolean;
    id: string;
    getTitle(): string;
}

export interface ICheckBoxDropdownProp {
    autoFocus?: boolean;
    classes?: string;
    buttonClasses?: string;
    disabled?: boolean;
    menuRight?: boolean;
    required?: boolean;
    selectedIds: string[];
    toggleAll?: boolean;
    toggleAllId?: string;
    values: ICheckBoxDropdownItem[];
    onChange: (selectedIds: string[]) => void;
}

export interface ICheckBoxDropdownState {
    open: boolean;
    selectedValueTitles: string;
}

export class CheckBoxDropdown extends React.Component<ICheckBoxDropdownProp, ICheckBoxDropdownState> {
    _isMounted: boolean;
    dropdownId: string;

    private selectedValueTitles = (props: ICheckBoxDropdownProp): string => {
        const selecedValues = props.values.filter(i => props.selectedIds.indexOf(i.id) >= 0);
        return selecedValues.map(i => i.getTitle()).join(", ");
    };

    constructor(props: ICheckBoxDropdownProp) {
        super(props);
        this.dropdownId = Base.getGuid();
        this.state = {
            open: false,
            selectedValueTitles: this.selectedValueTitles(props)
        };
    }

    dropdownClickEvent = (ev: MouseEvent) => {
        if (!this._isMounted) return;
        const element = ev.target as HTMLElement;
        const multiSelect = element.closest("[data-id='" + this.dropdownId + "']");
        if (!multiSelect) {
            this.setState({
                open: false
            });
        }
    };

    componentDidMount(): void {
        window.addEventListener("click", this.dropdownClickEvent, true);
        this._isMounted = true;
    }

    componentWillUnmount(): void {
        this._isMounted = false;
        window.removeEventListener("click", this.dropdownClickEvent);
    }

    componentDidUpdate(prevProps: ICheckBoxDropdownProp, prevState: ICheckBoxDropdownState): void {
        const selectedValueTitles = this.selectedValueTitles(this.props);
        if (prevState.selectedValueTitles === selectedValueTitles) return;
        this.setState({
            selectedValueTitles
        });
    }

    private getSelectedIds = (select: HTMLSelectElement): string[] => {
        const ids: string[] = [];
        for (let i = 0, l = select.selectedOptions.length; i < l; i++) {
            ids.push(select.selectedOptions[i].value);
        }
        return ids;
    };

    handleChange = (event) => {
        this.props.onChange(this.getSelectedIds(event.target as HTMLSelectElement));
    };

    handleClearClick = () => {
        const props = this.props;
        if (props.disabled) return;
        props.onChange([]);
    };

    handleToggleDropdown = () => {
        this.setState({
            open: !this.state.open
        });
    };

    handleCheckboxClickBoolean = (id: string, checked: boolean) => {
        //console.log("handleCheckboxClickBoolean", checked)
        const props = this.props;
        let selectedIds: string[];
        if (props.toggleAll) {
            selectedIds = Base.getToggledMultiSelectIds(props.values, props.selectedIds, id, checked, props.toggleAllId ?? "");
        } else {
            selectedIds = props.selectedIds.filter(i => i !== id);
            if (checked) {
                selectedIds.push(id);
            }
        }
        props.onChange(selectedIds);
    };

    render() {
        const props = this.props;
        const state = this.state;
        return (
            <div data-id={this.dropdownId} className="input-group checkboxDropdown">
                <div className="form-control" title={state.selectedValueTitles}>
                    <div className="selectedData" onClick={this.handleToggleDropdown}>{state.selectedValueTitles}</div>
                    {state.open &&
                        <div className="rbt-menu dropdown-menu show">
                            {props.values.map((value) =>
                                <CheckBox
                                    key={value.id}
                                    classes={"dropdown-item" + (value.classes ? " " + value.classes : "") + (value.disabled ? " disabled" : "")}
                                    disabled={props.disabled}
                                    checked={props.selectedIds.indexOf(value.id) > -1}
                                    title={value.getTitle()}
                                    onClickBoolean={(checked: boolean) => this.handleCheckboxClickBoolean(value.id, checked)}
                                />
                            )}
                        </div>
                    }
                </div>
                <span className="input-group-append">
                    {!props.disabled && !props.required && props.selectedIds && props.selectedIds.length > 0 && !props.toggleAll &&
                        <ToolButton
                            removeDefaultClasses={true}
                            tooltip={Translations.Clear}
                            classes="btn withIcon doClose"
                            disabled={false}
                            onClick={this.handleClearClick}
                        />
                    }
                    {!props.disabled &&
                        <ToolButton
                            removeDefaultClasses={true}
                            tooltip={Translations.Open}
                            classes="btn withIcon doExpand"
                            disabled={false}
                            onClick={this.handleToggleDropdown}
                        />
                    }
                </span>
            </div>
        );
    }
}

// TreeCheckBoxDropdownItems
// ***********************************************************************************************************************
export interface ITreeCheckBoxDropdownChildProp {
    isActive: boolean;
    item: ITreeCheckBoxDropdownItem;
    level: number;
    selectedIds: string[];
    singleSelect: boolean;
    onToggleItem: (item: ITreeCheckBoxDropdownItem, checked: boolean, hasChildren: boolean) => void;
}

class TreeCheckBoxDropdownChild extends React.Component<ITreeCheckBoxDropdownChildProp, {}> {
    render() {
        const props = this.props;
        const item = props.item;
        const hasChildren = item.hasChildren();
        const selected = props.selectedIds.indexOf(item.id) > -1;
        const disabled = item.disabled || !item.active;
        return (
            <div>
                {!props.singleSelect &&
                    <div className={"dropdown-tree-item leftPad level" + props.level.toString(10)} onClick={(e) => { e.stopPropagation(); e.preventDefault(); props.onToggleItem(item, !selected, hasChildren); }}>
                        <CheckBox
                            classes={"dropdown-item" + (item.classes ? " " + item.classes : "") + (disabled ? " disabled" : "")}
                            title={item.title}
                            checked={selected}
                            disabled={disabled}
                            onClickBoolean={(value: boolean) => props.onToggleItem(item, value, hasChildren)}
                        />
                    </div>
                }
                {!!props.singleSelect &&
                    <div className={"dropdown-tree-item leftPad level" + props.level.toString(10)} onClick={() => props.onToggleItem(item, true, false)}>
                        <div className={"dropdown-item text" + (item.classes ? " " + item.classes : "") + (hasChildren ? " subTitle" : (disabled ? " disabled" : "")) + (selected ? " active" : "")}><div>{item.title}</div></div>
                    </div>
                }
                {item.children.map((childItem) =>
                    <TreeCheckBoxDropdownChild
                        key={childItem.id}
                        isActive={true}
                        item={childItem}
                        level={props.level + 1}
                        selectedIds={props.selectedIds}
                        singleSelect={props.singleSelect}
                        onToggleItem={props.onToggleItem}
                    />
                )}
            </div>
        );
    }
}

export interface ITreeCheckBoxDropdownItem extends IIdTitleNode {
    classes?: string;
    disabled?: boolean;
}

export interface ITreeCheckBoxDropdownItemsProp {
    classes?: string;
    disabled?: boolean;
    selectedIds: string[];
    singleSelect?: boolean;
    toggleAllId?: string;
    values: ITreeCheckBoxDropdownItem[];
    onChange: (selectedIds: string[]) => void;
    onToggleDropdown?: () => void;
}

export class TreeCheckBoxDropdownItems extends React.Component<ITreeCheckBoxDropdownItemsProp, {}> {
    handleToggleItem = (item: ITreeCheckBoxDropdownItem, checked: boolean, hasChildren: boolean) => {
        if (!item) return;
        const disabled = item.disabled || !item.active;
        if (disabled) return;
        const props = this.props;
        const id = item.id;
        const allId = props.toggleAllId ?? Base.emptyGuid;
        let activeItemIds = props.selectedIds.filter(i => i !== allId);
        if (hasChildren) {
            const parent = IdTitleNode.getById(props.values, id);
            if (!parent) return;
            const childIds = parent.getActiveChildIds();
            activeItemIds = activeItemIds.filter(i => childIds.indexOf(i) < 0 && i !== id);
            if (checked) {
                activeItemIds.push(id);
                activeItemIds = activeItemIds.concat(childIds);
            }
        } else {
            if (id === allId) {
                if (checked) {
                    props.onChange([allId]);
                    return;
                } else if (activeItemIds.length < 1 && props.values.length > 1) {
                    activeItemIds.push(props.values[1].id);
                }
            } else {
                if (checked) {
                    if (activeItemIds.indexOf(id) < 0) {
                        activeItemIds.push(id);
                    }
                } else {
                    activeItemIds = activeItemIds.filter(i => i !== id);
                }
            }
        }
        if (activeItemIds.length < 1) {
            activeItemIds.push(allId);
        }
        props.onChange(activeItemIds);
    };

    handleSetSingleItem = (item: ITreeCheckBoxDropdownItem) => {
        const props = this.props;
        if (!item) return;
        const disabled = item.disabled || !item.active;
        if (disabled) return;
        props.onChange([item.id]);
        if (!props.onToggleDropdown) return;
        props.onToggleDropdown();
    };

    render() {
        const props = this.props;
        const singleSelect = !!props.singleSelect;
        return (
            <div className={"rbt-menu dropdown-menu show" + (props.classes ? " " + props.classes : "")}>
                {props.values.map((item) => {
                    const hasChildren = item.hasChildren();
                    const selected = props.selectedIds.indexOf(item.id) > -1;
                    const disabled = item.disabled || !item.active;
                    return <div key={item.id}>
                        {!singleSelect &&
                            <div className="dropdown-tree-item" onClick={(e) => { e.stopPropagation(); e.preventDefault(); this.handleToggleItem(item, !selected, hasChildren); }}>
                                <CheckBox
                                    key={item.id}
                                    classes={"dropdown-item" + (item.classes ? " " + item.classes : "") + (disabled ? " disabled" : "")}
                                    title={item.title}
                                    checked={selected}
                                    disabled={disabled}
                                    onClickBoolean={(value: boolean) => this.handleToggleItem(item, value, hasChildren)}
                                />
                            </div>
                        }
                        {singleSelect &&
                            <div className="dropdown-tree-item" onClick={() => this.handleSetSingleItem(item)}>
                                <div className={"dropdown-item text" + (item.classes ? " " + item.classes : "") + (hasChildren ? " subTitle" : (disabled ? " disabled" : "")) + (selected ? " active" : "")}><div>{item.title}</div></div>
                            </div>
                        }
                        {item.children.map((childItem) =>
                            <TreeCheckBoxDropdownChild
                                key={childItem.id}
                                isActive={true}
                                item={childItem}
                                level={1}
                                selectedIds={props.selectedIds}
                                singleSelect={singleSelect}
                                onToggleItem={!singleSelect ? this.handleToggleItem : (item: ITreeCheckBoxDropdownItem) => this.handleSetSingleItem(item)}
                            />
                        )}
                    </div>;
                })}
            </div>
        );
    }
}

// TreeCheckBoxDropdown
// ***********************************************************************************************************************
export interface ITreeCheckBoxDropdownProp {
    autoFocus?: boolean;
    classes?: string;
    buttonClasses?: string;
    disabled?: boolean;
    menuRight?: boolean;
    required?: boolean;
    selectedIds: string[];
    singleSelect?: boolean;
    toggleAllId?: string;
    hideParentsInSelection?: boolean;
    values: ITreeCheckBoxDropdownItem[];
    onChange: (selectedIds: string[]) => void;
}

export interface ITreeCheckBoxDropdownState {
    open: boolean;
    selectedValueTitles: string;
}

export class TreeCheckBoxDropdown extends React.Component<ITreeCheckBoxDropdownProp, ITreeCheckBoxDropdownState> {
    _isMounted: boolean;
    dropdownId: string;

    private selectedValueTitles = (props: ITreeCheckBoxDropdownProp): string => {
        let selecedValues = IdTitleNode.getByIds(props.values, props.selectedIds);
        if (props.hideParentsInSelection) {
            selecedValues = selecedValues.filter(i => !i.hasChildren());
        }
        return selecedValues.map(i => i.getTitle()).join(", ");
    };

    constructor(props: ITreeCheckBoxDropdownProp) {
        super(props);
        this.dropdownId = Base.getGuid();
        this.state = {
            open: false,
            selectedValueTitles: this.selectedValueTitles(props)
        };
    }

    dropdownClickEvent = (ev: MouseEvent) => {
        if (!this._isMounted) return;
        const element = ev.target as HTMLElement;
        const multiSelect = element.closest("[data-id='" + this.dropdownId + "']");
        if (!multiSelect) {
            this.setState({
                open: false
            });
        }
    };

    componentDidMount(): void {
        window.addEventListener("click", this.dropdownClickEvent, true);
        this._isMounted = true;
    }

    componentWillUnmount(): void {
        this._isMounted = false;
        window.removeEventListener("click", this.dropdownClickEvent);
    }

    componentDidUpdate(prevProps: ITreeCheckBoxDropdownProp, prevState: ITreeCheckBoxDropdownState): void {
        const selectedValueTitles = this.selectedValueTitles(this.props);
        if (prevState.selectedValueTitles === selectedValueTitles) return;
        this.setState({
            selectedValueTitles
        });
    }

    handleClearClick = () => {
        const props = this.props;
        if (props.disabled) return;
        props.onChange([]);
    };

    handleToggleDropdown = () => {
        this.setState({
            open: !this.state.open
        });
    };

    render() {
        const props = this.props;
        const state = this.state;
        return (
            <div data-id={this.dropdownId} className="input-group checkboxDropdown">
                <div className="form-control" title={state.selectedValueTitles}>
                    <div className="selectedData" onClick={this.handleToggleDropdown}>{state.selectedValueTitles}</div>
                    {state.open &&
                        <TreeCheckBoxDropdownItems
                            disabled={props.disabled}
                            selectedIds={props.selectedIds}
                            singleSelect={props.singleSelect}
                            toggleAllId={props.toggleAllId}
                            values={props.values}
                            onChange={props.onChange}
                            onToggleDropdown={this.handleToggleDropdown}
                        />
                    }
                </div>
                <span className="input-group-append">
                    {!props.disabled && !props.required && props.selectedIds && props.selectedIds.length > 0 &&
                        <ToolButton
                            removeDefaultClasses={true}
                            tooltip={Translations.Clear}
                            classes="btn withIcon doClose"
                            disabled={false}
                            onClick={this.handleClearClick}
                        />
                    }
                    {!props.disabled &&
                        <ToolButton
                            removeDefaultClasses={true}
                            tooltip={Translations.Open}
                            classes="btn withIcon doExpand"
                            disabled={false}
                            onClick={this.handleToggleDropdown}
                        />
                    }
                </span>
            </div>
        );
    }
}
